import { createSelector } from 'reselect';
import { createCachedSelector } from 're-reselect';
import { AppState } from '../../types/store';
import { ModulePriv, ModuleType, Modules } from '../../types/modules';

const reduxKey = 'modules';
export const getModules = (state: any): Modules => {
  if (!state[reduxKey]) {
    return {} as any;
  }
  return state[reduxKey];
};

export const findModules = (modules: Modules, predicate: (m: any) => boolean) =>
  Object.keys(modules)
    .filter(key => predicate(modules[key]))
    .map(key => modules[key]);

export const getModule = (state: AppState, moduleId: number) =>
  getModules(state)[moduleId] || {};

export const getModuleIds = createSelector(getModules, modules =>
  Object.keys(modules)
);

export const getModuleType = (state: AppState, moduleId: number) =>
  getModules(state)[moduleId]?.type;
export const getModuleVersion = (state: AppState, moduleId: number) =>
  getModules(state)[moduleId]?.version;
export const getModuleData = (state: AppState, moduleId: number) =>
  getModules(state)[moduleId]?.moduleData || {};
export const getModulePrivileges = (state: AppState, moduleId: number) =>
  getModules(state)[moduleId]?.privileges || {};
export const getModuleConfig = (state: AppState, moduleId: number) =>
  getModules(state)[moduleId]?.moduleConfig || {};
export const getModuleMeta = (state: AppState, moduleId: number) =>
  getModules(state)[moduleId]?.moduleMeta || {};

const getModuleTypeArgument = (state: AppState, moduleType: string) =>
  moduleType;

export const getAllModulesByModuleType = createCachedSelector(
  getModules,
  getModuleTypeArgument,
  (modules, moduleType) => findModules(modules, m => m.type === moduleType)
)(getModuleTypeArgument); // cache by moduleType

export const getModuleByModuleType = createCachedSelector(
  getAllModulesByModuleType,
  modules => modules.pop()
)(getModuleTypeArgument);

export const getModuleDataByModuleType = createCachedSelector(
  getModuleByModuleType,
  module => module?.moduleData || {}
)(getModuleTypeArgument);

export const getModuleIdByModuleType = createCachedSelector(
  getModuleByModuleType,
  module => module?.id || undefined
)(getModuleTypeArgument);

export const getAllModuleTypeByModuleId = createSelector(getModules, modules =>
  Object.keys(modules).reduce((result, moduleId) => {
    result[moduleId] = modules[moduleId].type;
    return result;
  }, {} as any)
);

export const hasModuleTypeInstalled = createCachedSelector(
  getAllModulesByModuleType,
  (modules): boolean => !!modules.length
)(getModuleTypeArgument);

// cannot re-use selector from layout.ts due to cyclic dependency
export const getAllInstalledModuleIdsInSection = (
  state: AppState,
  sectionName: string
) => {
  const zones = state.layout?.[sectionName]?.zones || {};
  return Object.keys(zones).reduce(
    (acc, zoneName) => acc.concat(zones[zoneName]?.installedModulesIds || []),
    [] as number[]
  );
};

export const getSectionModules = createSelector(
  getModules,
  getAllInstalledModuleIdsInSection,
  (modules, allInstalledModuleIds) =>
    findModules(modules, m => allInstalledModuleIds.includes(m.id))
);

export const getSectionModuleDataByModuleType = createSelector(
  getSectionModules,
  getAllInstalledModuleIdsInSection,
  (state: AppState, sectionName: string, moduleType: string) => moduleType,
  (modules, allInstalledModuleIds, moduleType) => {
    const sectionModules = findModules(modules, m =>
      allInstalledModuleIds.includes(m.id)
    );
    const moduleRaw = sectionModules.find(m => m.type === moduleType);
    return moduleRaw?.moduleData || {};
  }
);

export const canPerformModuleAction = (
  state: AppState,
  moduleId: number,
  priv: ModulePriv
) => getModulePrivileges(state, moduleId)[priv] !== false; // assume true by default, this is dangerous, but until all the actions are checked against privileges we need to maintain backwards compatibility

export const canEditCoverDeviation = state =>
  canPerformModuleAction(
    state,
    getModuleIdByModuleType(state, ModuleType.COVER_DEVIATION),
    ModulePriv.EDIT
  );
